import { pipelineConstant } from "../constants/pipelineConstant";
import type { PipelineWithStagesAndTags } from "../../containers/Pipelines/types";

interface PipelineState {
  pipeline: PipelineWithStagesAndTags | null;  // The single pipeline for this agency
  loading: boolean;
  error: any | null;
}

interface PipelineAction {
  type: string;
  data?: any;
  payload?: any;
  error?: any;
}

const initialState: PipelineState = {
  pipeline: null,
  loading: false,
  error: null,
};

export default function pipeline(state = initialState, action: PipelineAction): PipelineState {
  switch (action.type) {
    // Get Default Pipeline (Single Pipeline Mode)
    case pipelineConstant.GET_DEFAULT_PIPELINE_R:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case pipelineConstant.GET_DEFAULT_PIPELINE_S: {
      const pipeline = action.data?.data?.pipeline;
      return {
        ...state,
        pipeline: pipeline || null,
        loading: false,
      };
    }

    case pipelineConstant.GET_DEFAULT_PIPELINE_F:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    // Update Pipeline
    case pipelineConstant.UPDATE_PIPELINE_R:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case pipelineConstant.UPDATE_PIPELINE_S: {
      const updatedPipeline = action.data?.data?.pipeline;
      return {
        ...state,
        pipeline: updatedPipeline || state.pipeline,
        loading: false,
      };
    }

    case pipelineConstant.UPDATE_PIPELINE_F:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    // Create Stage
    case pipelineConstant.CREATE_STAGE_R:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case pipelineConstant.CREATE_STAGE_S: {

      // Add the new stage to the pipeline without refetching
      const newStage = action.data?.data?.stage;
      if (newStage && state.pipeline) {
        // Normalize the stage data to ensure consistency
        const normalizedStage = {
          ...newStage,
          deleted: Boolean(newStage.deleted), // Ensure boolean
          id: Number(newStage.id), // Ensure number
        };

        // Create new arrays/objects to ensure React detects the change
        const newStages = state.pipeline.stages.concat(normalizedStage);
        const newPipeline = {
          ...state.pipeline,
          stages: newStages,
        };

        return {
          ...state,
          pipeline: newPipeline,
          loading: false,
        };
      }
      return {
        ...state,
        loading: false,
      };
    }

    case pipelineConstant.CREATE_STAGE_F:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    case pipelineConstant.UPDATE_STAGE_R:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case pipelineConstant.UPDATE_STAGE_S: {
      const updatedPipeline = action.data?.data?.pipeline;
      return {
        ...state,
        pipeline: updatedPipeline || state.pipeline,
        loading: false,
      };
    }

    case pipelineConstant.UPDATE_STAGE_F:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    case pipelineConstant.DELETE_STAGE_R:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case pipelineConstant.DELETE_STAGE_S: {
      const updatedPipeline = action.data?.data?.pipeline;
      return {
        ...state,
        pipeline: updatedPipeline || state.pipeline,
        loading: false,
      };
    }

    case pipelineConstant.DELETE_STAGE_F:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    // Delete Pipeline
    case pipelineConstant.DELETE_PIPELINE_R:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case pipelineConstant.DELETE_PIPELINE_S: {
      return {
        ...state,
        pipeline: null,
        loading: false,
      };
    }

    case pipelineConstant.DELETE_PIPELINE_F:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    // Reorder Stages (Optimistic Update - Trello/Asana pattern)
    case pipelineConstant.REORDER_STAGES_OPTIMISTIC: {
      if (!state.pipeline || !action.payload?.stages) return state;

      // Create a map of stage id -> new order_index
      const orderMap = new Map<number, number>(
        action.payload.stages.map((s: { id: number; order_index: number }) => [s.id, s.order_index])
      );

      // Update order_index for all stages
      const reorderedStages = state.pipeline.stages
        .map(stage => ({
          ...stage,
          order_index: orderMap.get(stage.id) ?? stage.order_index
        }))
        .sort((a, b) => (a.order_index as number) - (b.order_index as number)); // Sort by new order

      return {
        ...state,
        pipeline: {
          ...state.pipeline,
          stages: reorderedStages,
        },
      };
    }

    // Set Selected Pipeline (kept for compatibility, just updates pipeline)
    case pipelineConstant.SET_SELECTED_PIPELINE:
      return {
        ...state,
        pipeline: action.data,
      };

    // Clear State
    case pipelineConstant.CLEAR_PIPELINE_STATE:
      return initialState;

    default:
      return state;
  }
}
